import { endOfDay, format, startOfDay } from "date-fns";
import { cloneDeep, groupBy } from "lodash";
import { TDateRange } from "../../../component/common/ZFDateRangePicker/defaultStaticRanges";
import {
	TFilterDropDownConfig,
	TFilterDropDownData,
} from "../../../fixtures/data-fixtures/ComponentConfigFixtures";
import { Pick_A } from "../../../fixtures/utilityFixtures";
import { defaultPageProperties } from "../fixtures";
import { TOrderTableData, TOrderTabTypes } from "../orderDataProvider";
import {
	IOrderListingConfig,
	IOrderListingHeader,
	IPageProperties,
} from "./OrderListingTable/OrderListingTable.d";
import {
	getTableConfig,
	TOrderModuleTabs,
	TOrderTableTab,
} from "./OrderModule.utils";

const tabs: TOrderTableTab[] = [
	{
		title: "LOADING...",
		tabType: "in_progress",
		enableDetailView: true,
		table: {
			config: {
				columnMetaData: [] as Array<any>,
				orderListingConfig: {
					orderItems: [],
					totalRecords: 0,
				} as IOrderListingConfig,
				headers: [] as Array<IOrderListingHeader>,
			},
		},
		filters: {
			config: [] as TFilterDropDownConfig[],
			data: {
				selected: [] as TFilterDropDownData[],
				rest: [] as TFilterDropDownData[],
				basedOnSelection: [] as TFilterDropDownData[],
				all: [] as TFilterDropDownData[],
			},
		},
		pageProperties: { ...defaultPageProperties },
		dateProperties: {
			config: ["TODAY", "YESTERDAY", "LAST_7_DAYS"],
			dateRange: {
				startDate: startOfDay(new Date()),
				endDate: endOfDay(new Date()),
			},
		},
	},
];

export const INIT_ORDER_STATE = {
	activeTabIndex: 0,
	tabs: tabs,
};

export type TOrderFilter = Pick_A<TOrderTableTab, "filters">;
export type TOrderFilterConfig = Pick_A<TOrderFilter, "config">[number];
export type TDateProperties = Pick_A<TOrderTableTab, "dateProperties">;

export type TOrderFilterData = Pick_A<TOrderFilter, "data">;

export type TOrderState = typeof INIT_ORDER_STATE;

export type TOrderActionType =
	| "PAGE_PROPERTIES"
	| "TAB_CHANGE"
	| "FILTER_CHANGE"
	| "FILTER_UPDATE_BY_TAB"
	| "INIT"
	| "TABLE_DATA_UPDATE"
	| "DATE_UPDATE";

export const pagePropertiesUpdateDTO = {
	type: "PAGE_PROPERTIES" as TOrderActionType,
	payload: { pageProperties: { ...defaultPageProperties } },
};

export type TPagePropertiesUpdateDTO = typeof pagePropertiesUpdateDTO;

export const tabUpdateDTO = {
	type: "TAB_CHANGE" as TOrderActionType,
	payload: {
		activeTabIndex: 0,
	},
};

export type TTabUpdateUpdateDTO = typeof tabUpdateDTO;

export const filterUpdateDTO = {
	type: "FILTER_CHANGE" as TOrderActionType,
	payload: { filters: { ...INIT_ORDER_STATE.tabs[0].filters.data } },
};

export type TFilterUpdateDTO = typeof filterUpdateDTO;

export const init = {
	type: "INIT" as TOrderActionType,
	payload: {
		tabs: {
			...INIT_ORDER_STATE.tabs,
		},
	},
};

export const data = {
	type: "TABLE_DATA_UPDATE" as TOrderActionType,
	payload: {
		data: {} as TOrderTableData,
	},
};

export const dateProperties = {
	type: "DATE_UPDATE" as TOrderActionType,
	payload: {
		dateRange: {} as TDateRange,
	},
};

export const filterDetailsByTabUpdate = {
	type: "DATE_UPDATE" as TOrderActionType,
	payload: {
		tabIndex: 0,
		filters: { ...INIT_ORDER_STATE.tabs[0].filters.data },
	},
};

export type TFilterDetailsByTabUpdateDTO = typeof filterDetailsByTabUpdate;

export type TOrderDateParams = typeof dateProperties;

export type TOrderStateDataUpdate = typeof data;

export type TOrderStateInit = typeof init;

export type TOrderActionPayLoad =
	| TPagePropertiesUpdateDTO
	| TFilterUpdateDTO
	| TTabUpdateUpdateDTO
	| TOrderStateInit
	| TOrderStateDataUpdate
	| TOrderDateParams
	| TFilterDetailsByTabUpdateDTO;

export const orderStateReducer = (
	state: OrderComponentState,
	orderActionPayloads: TOrderActionPayLoad[]
) => {
	const orderStateUpdate = orderActionPayloads.reduce(
		(acc: OrderComponentState, curr: TOrderActionPayLoad) => {
			const getNewOrderState = () =>
				new OrderComponentState(acc.activeTabIndex, acc.tabs);
			switch (curr.type) {
				case "FILTER_CHANGE":
					const x1 = getNewOrderState();
					x1.updateFilterData({
						...(curr as TFilterUpdateDTO).payload.filters,
					});
					return x1;
				case "TAB_CHANGE":
					const x2 = getNewOrderState();
					x2.updateActiveTab(
						(curr as TTabUpdateUpdateDTO).payload.activeTabIndex
					);
					return x2;
				case "PAGE_PROPERTIES":
					const x3 = getNewOrderState();
					x3.updatePageProperties({
						...(curr as TPagePropertiesUpdateDTO).payload
							.pageProperties,
					});
					return x3;
				case "INIT":
					const x31 = new OrderComponentState(0, [
						...((curr as TOrderStateInit).payload
							.tabs as TOrderModuleTabs),
					]);
					x31.loading = false;
					return x31;
				case "FILTER_UPDATE_BY_TAB":
					const x11 = getNewOrderState();
					const y = {
						...(curr as TFilterDetailsByTabUpdateDTO).payload,
					};
					x11.updateFilterDataByTabIndex(y.tabIndex, y.filters);
					return x11;
				case "TABLE_DATA_UPDATE":
					const x4 = getNewOrderState();
					x4.updateOrderListingConfig(
						(curr as TOrderStateDataUpdate).payload.data
					);
					return x4;
				case "DATE_UPDATE":
					const x5 = getNewOrderState();
					const p = (curr as TOrderDateParams).payload.dateRange;
					x5.updateDateParams(p);
					return x5;
				default:
					return acc;
			}
		},
		state
	);
	return orderStateUpdate;
};

export class OrderComponentState {
	public activeTabIndex: number;
	private lastUpdateTime: Date = new Date();
	public tabs: TOrderModuleTabs;
	public loading = true;

	constructor(i: number = 0, tabsData: TOrderModuleTabs) {
		this.activeTabIndex = i;
		this.tabs = tabsData;
	}

	getActiveTab() {
		return this.tabs[this.activeTabIndex];
	}

	updateActiveTab(i: number) {
		this.activeTabIndex = i;
		return this.getActiveTab();
	}

	updatePageProperties(page: IPageProperties) {
		this.getActiveTab().pageProperties = { ...page };
	}

	updateFilterData(filterData: TOrderFilterData) {
		this.getActiveTab().filters.data = { ...filterData };
	}

	updateFilterDataByTabIndex(index: number, filterData: TOrderFilterData) {
		this.tabs[index].filters.data = { ...filterData };
	}

	updateOrderListingConfig(data: TOrderTableData) {
		console.log(
			"updateOrderListingConfig",
			cloneDeep(this.getActiveTab()),
			data
		);
		this.getActiveTab().table.config.orderListingConfig = {
			...getTableConfig(
				this.getActiveTab().table.config.columnMetaData,
				data
			),
		};
		this.updateLastUpdatedTime();
	}

	updateDateParams(dateRange: TDateRange) {
		const x = this.getActiveTab().dateProperties;

		this.getActiveTab().dateProperties = { ...x, dateRange: dateRange };
	}

	updateLastUpdatedTime() {
		this.lastUpdateTime = new Date();
	}

	getLastUpdateTime() {
		return format(this.lastUpdateTime, "MMM dd h:mm a");
	}

	getDateProperties() {
		return { ...this.getActiveTab().dateProperties };
	}

	getAllTabs() {
		return this.tabs.map((_: any) => {
			return { title: _.title };
		});
	}

	getFilters() {
		return this.getActiveTab().filters.data;
	}

	getFilterConfig() {
		return this.getActiveTab().filters.config;
	}

	getTabPageProperties() {
		return this.getActiveTab().pageProperties;
	}

	getOrderRequestParams(requestParams: {
		selected?: TFilterDropDownData[];
		pageIndex?: number;
		limit?: number;
		dateRange?: TDateRange;
		tabIndex?: number;
		tabType?: TOrderTabTypes;
	}) {
		const {
			pageIndex,
			limit,
			tabIndex,
			dateRange,
			tabType,
		} = requestParams;

		const activeTabIndex =
			tabIndex !== null && tabIndex !== undefined
				? tabIndex
				: this.activeTabIndex;
		const activeTab = this.tabs[activeTabIndex];

		const selected =
			requestParams.selected || activeTab.filters.data.selected;
		const groupedFilters = groupBy(selected, "dimension");
		const requestFilter = Object.keys(groupedFilters).map((k) => {
			return { key: k, values: groupedFilters[k].map((_) => _.value) };
		});

		const dateRangeF = dateRange || activeTab.dateProperties.dateRange;
		const [startDate, endDate] = [
			dateRangeF.startDate,
			dateRangeF.endDate,
		].map((d) => format(d, "yyyy-MM-dd"));

		console.log(
			"getOrderRequestParams",
			dateRangeF.startDate,
			dateRangeF.endDate,
			{ startDate, endDate }
		);

		return {
			filters: requestFilter,
			tabType: tabType || activeTab.tabType,
			pageIndex: pageIndex || activeTab.pageProperties.pageNo,
			limit: limit || activeTab.pageProperties.itemsPerPage,
			startDate,
			endDate,
		};
	}

	getOrderFilterRequestParams(requestParams: {
		selectedFilters?: TFilterDropDownData[];
		dateRange?: TDateRange;
		filterKeys?: string[];
		tabType: TOrderTabTypes;
	}) {
		const tab = this.getActiveTab();
		const filterKeys =
			requestParams.filterKeys ||
			tab.filters.config.map((_: any) => _.key);

		const groupedFilters = groupBy(
			requestParams.selectedFilters,
			"dimension"
		);
		const selectedFiltersFormatted = Object.keys(groupedFilters).reduce(
			(acc, curr) => {
				acc[curr] = groupedFilters[curr].map((_) => _.value);
				return acc;
			},
			{}
		);

		const dateRangeF =
			requestParams.dateRange || this.getDateProperties().dateRange;

		const delivery_date = [
			dateRangeF.startDate,
			dateRangeF.endDate,
		].map((d) => format(d, "dd/MM/yyyy"));

		const selectedFilters1 = {
			...selectedFiltersFormatted,
			delivery_date: delivery_date,
			status: [requestParams.tabType],
		};

		console.log({
			selectedFilters: selectedFilters1,
			filterKeys,
		});

		return getOrderFilterRequestParamsEXT({
			selectedFilters: selectedFilters1,
			filterKeys,
		});
	}
}

export const getOrderFilterRequestParamsEXT = (requestParams: {
	filterKeys: string[];
	selectedFilters?: object;
}) => {
	return {
		selectedFilters: new Map(),
		...requestParams,
	};
};
